import { type Icon } from '@tabler/icons-react';
import { ComponentPropsWithoutRef, FunctionComponent } from 'react';

import { cn } from '../../utils/cn';
import { Loader } from '../feedback/loader';

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  readonly variant?: 'primary' | 'secondary' | 'confirmative' | 'destructive';
  readonly loading?: boolean;
  readonly leadingIcon?: Icon;
  readonly trailingIcon?: Icon;
}

export const Button: FunctionComponent<ButtonProps> = (props) => {
  const {
    variant = 'primary',
    loading = false,
    leadingIcon: LeadingIcon,
    trailingIcon: TrailingIcon,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
  } = props;

  const loaderVariant = variant === 'secondary' ? 'default' : 'gray';

  return (
    <button
      type={type}
      className={cn(
        'inline-flex h-[var(--size-brand-control)] items-center justify-center gap-x-1.5 rounded-[var(--radius-brand-control)] border px-[var(--space-brand-control-x)] text-[length:var(--font-size-brand-button)] font-semibold leading-[1.2] shadow-xs transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dotted disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300 disabled:text-white',
        {
          'border-brand-blue-700 bg-brand-blue-700 text-white hover:border-brand-blue-800 hover:bg-brand-blue-800 focus-visible:outline-brand-blue-700':
            variant === 'primary',
          'border-brand-blue-700 bg-white text-brand-blue-700 hover:bg-brand-blue-700 hover:text-white focus-visible:outline-brand-blue-700':
            variant === 'secondary',
          'border-brand-green-700 bg-brand-green-700 text-white hover:border-brand-green-800 hover:bg-brand-green-800 focus-visible:outline-brand-green-700':
            variant === 'confirmative',
          'border-red-600 bg-red-600 text-white hover:border-red-700 hover:bg-red-700 focus-visible:outline-red-600':
            variant === 'destructive',
        },
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Loader aria-hidden="true" className="-ml-0.5 size-5 p-0.5" variant={loaderVariant} /> : null}
      {LeadingIcon && !loading ? <LeadingIcon aria-hidden="true" className="-ml-0.5 size-5" strokeWidth={1.5} /> : null}

      <span>{children}</span>

      {TrailingIcon ? <TrailingIcon aria-hidden="true" className="-mr-0.5 size-5" strokeWidth={1.5} /> : null}
    </button>
  );
};
